import {
  DialogProps,
  DialogContent,
  makeStyles,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import * as React from 'react';
import { DialogControls } from 'components';
import { FiscalService } from 'services';
import { PaymentTypeLabel } from 'enums';

const useStyles = makeStyles((theme) => ({
  successColor: { color: theme.palette.success.main },
  errorColor: { color: theme.palette.error.main },
  centreAlign: { textAlign: 'center' },
  receiptNo: {
    marginTop: theme.spacing(1),
    fontFamily: 'monospace',
    fontSize: '1.1rem',
  },
  note: { marginTop: theme.spacing(1), color: theme.palette.text.secondary },
}));

interface Props {
  orderId?: number;
  paymentType?: number;
  dialogProps: DialogProps;
}

/**
 * The last step of a manual order.
 *
 * This used to fire the BROWSER's print dialog a second after the order was
 * placed, which produced the old pre-fiscal slip on whatever printer Windows
 * happened to have. That is not a fiscal receipt and cannot be given to a
 * customer (Lucy, 2026-10-08: "im getting the windows print prompt with a
 * receipt that we used to use").
 *
 * It now cashes the order properly: the server allocates the fiscal receipt
 * number, writes it to the ledger and prints it on that outlet's own till
 * printer. A Street Food order therefore prints a Street Food receipt without
 * anyone picking a printer.
 *
 * The call is idempotent server-side, so a re-render cannot allocate a second
 * number for the same order.
 */
export const FinishStep: React.FC<Props> = ({
  dialogProps,
  orderId,
  paymentType,
}) => {
  const classes = useStyles();
  const [busy, setBusy] = React.useState(false);
  const [receiptNo, setReceiptNo] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState<string | null>(null);
  const done = React.useRef(false);

  React.useEffect(() => {
    if (!orderId || done.current) {
      return;
    }
    done.current = true;
    setBusy(true);

    FiscalService.issueOrder(orderId, PaymentTypeLabel[paymentType ?? 2] ?? 'Cash')
      .then((r) => {
        setReceiptNo(r.receiptNo ?? null);
        setBusy(false);
      })
      .catch((e) => {
        // The order is already saved. A receipt that did not print is a
        // problem to flag, not a reason to pretend the order failed.
        setFailed(e?.message ?? 'The receipt could not be printed.');
        setBusy(false);
      });
  }, [orderId, paymentType]);

  return (
    <React.Fragment>
      <DialogContent className={classes.centreAlign}>
        {failed ? (
          <ErrorOutlineIcon style={{ fontSize: 60 }} className={classes.errorColor} />
        ) : (
          <CheckCircleOutlineIcon
            style={{ fontSize: 60 }}
            className={classes.successColor}
          />
        )}
        <br />
        <Typography
          variant="h6"
          className={failed ? classes.errorColor : classes.successColor}
        >
          Order has been placed!
        </Typography>

        {busy && (
          <Typography variant="body2" className={classes.note}>
            <CircularProgress size={14} style={{ marginRight: 8 }} />
            Printing the fiscal receipt…
          </Typography>
        )}

        {receiptNo && (
          <Typography className={classes.receiptNo}>
            Receipt {receiptNo}
          </Typography>
        )}

        {failed && (
          <Typography variant="body2" className={classes.note}>
            The order is saved, but the receipt did not print: {failed}
          </Typography>
        )}
      </DialogContent>

      <DialogControls onClose={dialogProps.onClose} />
    </React.Fragment>
  );
};
